import { createApi } from "@reduxjs/toolkit/query/react";
import { parseISO, startOfToday } from "date-fns";

import { baseQuery } from "../../baseQuery";

type PublicHolidayEventResponse = {
  public_holiday_events: PublicHolidayEvent[];
};

type PublicHolidayRecommendationResponse = {
  public_holiday_recommendations: PublicHolidayRecommendation[];
};

type PublicHolidayRecommendation = {
  date: string;
  name: string;
};

const publicHolidayApi = createApi({
  reducerPath: "publicHolidayApi",
  baseQuery: baseQuery,
  tagTypes: ["PublicHolidayEvent"],
  endpoints: (builder) => ({
    fetchPublicHolidayEvents: builder.query<PublicHolidayEventResponse, void>({
      query: () => "/api/public_holiday",
      providesTags: (result) => {
        return result
          ? [
              ...result.public_holiday_events.map((event) => ({
                type: "PublicHolidayEvent" as const,
                // TODO(store): this id tag doesn't do anything yet because we always invalidate the whole list
                id: event.id,
              })),
              { type: "PublicHolidayEvent", id: "LIST" },
            ]
          : [{ type: "PublicHolidayEvent", id: "LIST" }];
      },
    }),
    fetchPublicHolidayRecommendations: builder.query<PublicHolidayRecommendation[], void>({
      query: () => ({
        url: "/api/public_holiday/recommendations",
      }),
      transformResponse: (response: PublicHolidayRecommendationResponse) => {
        return response.public_holiday_recommendations.filter((recommendation) => {
          const dateObject = parseISO(recommendation.date);
          // Filter out public holidays in the past
          return dateObject >= startOfToday();
        });
      },
    }),
    createPublicHolidayEvent: builder.mutation<PublicHolidayEvent, Partial<PublicHolidayEvent>>({
      query: (event) => ({
        url: "/api/public_holiday",
        method: "POST",
        body: event,
      }),
      invalidatesTags: [{ type: "PublicHolidayEvent", id: "LIST" }],
    }),
    updatePublicHolidayEvent: builder.mutation<
      PublicHolidayEvent,
      { id: PublicHolidayEvent["id"]; data: Partial<PublicHolidayEvent> }
    >({
      query: ({ id, data }) => ({
        url: `/api/public_holiday/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PublicHolidayEvent", id },
        { type: "PublicHolidayEvent", id: "LIST" },
      ],
    }),
    deletePublicHolidayEvent: builder.mutation<void, PublicHolidayEvent["id"]>({
      query: (id) => ({
        url: `/api/public_holiday/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PublicHolidayEvent", id },
        { type: "PublicHolidayEvent", id: "LIST" },
      ],
    }),
  }),
});

export { publicHolidayApi };
export const {
  useFetchPublicHolidayEventsQuery,
  useFetchPublicHolidayRecommendationsQuery,
  useCreatePublicHolidayEventMutation,
  useUpdatePublicHolidayEventMutation,
  useDeletePublicHolidayEventMutation,
} = publicHolidayApi;
